

class ObjectIdManager {

  /**
   * 验证ObjectId格式是否有效
   * @param {string} objectId - 要验证的ObjectId
   * @returns {boolean} 是否为有效的ObjectId格式
   */
  isValidObjectId(objectId) {
    if (!objectId || typeof objectId !== 'string') {
      return false;
    }
    
    // MongoDB ObjectId是24位十六进制字符串
    const objectIdPattern = /^[0-9a-fA-F]{24}$/;
    return objectIdPattern.test(objectId);
  }

  // 移除了所有cookie存储相关的方法
  // 表单ID和标题现在通过sessionStorage在formDataStorage中管理

  /**
   * 从URL路径中提取ObjectId
   * @param {string} path - URL路径
   * @returns {string|null} 提取的ObjectId，如果没有则返回null
   */
  extractObjectIdFromPath(path) {
    // 匹配 /application/{objectId} 格式的路径
    const match = path.match(/\/application\/([0-9a-fA-F]{24})/);
    if (match && this.isValidObjectId(match[1])) {
      return match[1];
    }
    return null;
  }

  /**
   * 生成ObjectId的显示格式（用于UI显示）
   * @param {string} objectId - 完整的ObjectId或其他ID格式
   * @returns {string} 格式化的ID（前8位...后8位）
   */
  formatObjectIdForDisplay(objectId) {
    // 如果objectId为空或不是字符串，返回空字符串
    if (!objectId || typeof objectId !== 'string') {
      return '';
    }
    
    // 如果是有效的ObjectId格式，使用原有的格式化逻辑
    if (this.isValidObjectId(objectId)) {
      if (objectId.length <= 16) {
        return objectId;
      }
      return `${objectId.substring(0, 8)}...${objectId.substring(objectId.length - 8)}`;
    }
    
    // 如果不是ObjectId格式，但仍然是一个有效的字符串ID，直接返回
    // 这样可以处理后端返回的非ObjectId格式的application_id
    if (objectId.trim() !== '') {
      if (objectId.length <= 16) {
        return objectId;
      }
      return `${objectId.substring(0, 8)}...${objectId.substring(objectId.length - 8)}`;
    }
    
    // 如果确实是无效的ID，返回空字符串而不是"无效ID"
    return '';
  }

  /**
   * 检查两个ObjectId是否相等
   * @param {string} objectId1 - 第一个ObjectId
   * @param {string} objectId2 - 第二个ObjectId
   * @returns {boolean} 是否相等
   */
  areObjectIdsEqual(objectId1, objectId2) {
    if (!this.isValidObjectId(objectId1) || !this.isValidObjectId(objectId2)) {
      return false;
    }
    
    return objectId1.toLowerCase() === objectId2.toLowerCase();
  }

  /**
   * 获取ObjectId的创建时间（基于ObjectId的前4字节）
   * @param {string} objectId - ObjectId
   * @returns {Date|null} 创建时间，如果无效则返回null
   */
  getObjectIdTimestamp(objectId) {
    if (!this.isValidObjectId(objectId)) {
      return null;
    }
    
    try {
      // ObjectId的前4字节是时间戳（Unix时间戳，秒）
      const timestamp = parseInt(objectId.substring(0, 8), 16);
      return new Date(timestamp * 1000);
    } catch (error) {
      console.error('❌ 解析ObjectId时间戳失败:', error);
      return null;
    }
  }

  /**
   * 获取ObjectId的年龄（从创建到现在的时间）
   * @param {string} objectId - ObjectId
   * @returns {Object|null} 包含天数和小时数的对象，如果无效则返回null
   */
  getObjectIdAge(objectId) {
    const timestamp = this.getObjectIdTimestamp(objectId);
    if (!timestamp) {
      return null;
    }
    
    const now = new Date();
    const diffMs = now - timestamp;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return {
      days: diffDays,
      hours: diffHours,
      totalHours: Math.floor(diffMs / (1000 * 60 * 60))
    };
  }
}

// 创建单例实例
const objectIdManager = new ObjectIdManager();


export const isValidObjectId = objectIdManager.isValidObjectId.bind(objectIdManager);
export const extractObjectIdFromPath = objectIdManager.extractObjectIdFromPath.bind(objectIdManager);
export const formatObjectIdForDisplay = objectIdManager.formatObjectIdForDisplay.bind(objectIdManager);
export const areObjectIdsEqual = objectIdManager.areObjectIdsEqual.bind(objectIdManager);
export const getObjectIdTimestamp = objectIdManager.getObjectIdTimestamp.bind(objectIdManager);
export const getObjectIdAge = objectIdManager.getObjectIdAge.bind(objectIdManager);

export default objectIdManager;
